import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Timer } from "lucide-react";

const flow = [
  "/schools",
  "/schools/student",
  "/schools/parent",
  "/schools/counsellor",
  "/schools/studios",
  "/schools/portfolio",
];

/**
 * Small pill shown only in presenter mode. Displays the current demo
 * step (X of 6) and an elapsed timer so the presenter can pace the room
 * without looking at their watch.
 */
export default function PresenterHud({ on }: { on: boolean }) {
  const { pathname } = useLocation();
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const [now, setNow] = useState<number>(Date.now());

  // Reset timer whenever presenter mode is (re)entered.
  useEffect(() => {
    if (on) setStartedAt(Date.now());
  }, [on]);

  useEffect(() => {
    if (!on) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [on]);

  const step = useMemo(() => {
    const idx = flow.findIndex((p) => (p === "/schools" ? pathname === p : pathname.startsWith(p)));
    return idx >= 0 ? idx + 1 : null;
  }, [pathname]);

  if (!on) return null;

  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div
      className="fixed bottom-4 right-4 z-40 no-print flex items-center gap-2 rounded-full border bg-background/85 backdrop-blur px-3 py-1.5 text-xs shadow-lg"
      role="status"
      aria-live="polite"
    >
      {step && (
        <>
          <span className="font-medium">Step {step}</span>
          <span className="text-muted-foreground">of {flow.length}</span>
          <span className="w-px h-3 bg-border" />
        </>
      )}
      <Timer className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="tabular-nums font-medium">{mm}:{ss}</span>
    </div>
  );
}
